import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Share2, 
  Loader2, 
  Link2, 
  Copy, 
  Check, 
  Facebook, 
  Building2,
  ExternalLink,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const platformIcons = {
  meta: Facebook,
  google: Building2
};

const PostAssetDialog = ({ isOpen, onClose, asset, caption, cta }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [copied, setCopied] = useState(false);
  const [posting, setPosting] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchConnections();
    }
  }, [isOpen]);

  const fetchConnections = async () => {
    setLoadingConnections(true);
    try {
      const response = await axios.get(`${API_URL}/api/platforms/status`);
      setConnections(response.data.connections || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleShareNative = async () => {
    const shareText = caption + '\n\n' + cta;

    try {
      if (navigator.share) {
        await navigator.share({ title: 'Sales Asset', text: shareText });
        toast.success('Shared!');
        onClose();
      } else {
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Caption copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Sharing not supported');
      }
    }
  };

  const copyCaption = async () => {
    const shareText = caption + '\n\n' + cta;
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Caption copied!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleConnectPlatform = () => {
    // Store asset ID for return
    if (asset?.id) {
      localStorage.setItem('oauth_return_asset', asset.id);
    }
    navigate('/connections');
    onClose();
  };

  const tier = user?.subscription_tier || 'free';
  const isFree = tier === 'free';
  const hasConnections = connections.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0a2540]">Share Your Asset</DialogTitle>
          <DialogDescription>
            Post to social media or copy the caption
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Quick Copy Button */}
          <Button
            onClick={copyCaption}
            variant="outline"
            className="w-full h-12 border-2"
            data-testid="copy-caption-btn"
          >
            {copied ? (
              <>
                <Check className="w-5 h-5 mr-2 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5 mr-2" />
                Copy Caption
              </>
            )}
          </Button>

          {/* Native Share */}
          {'share' in navigator && (
            <Button
              onClick={handleShareNative}
              className="w-full h-12 bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028]"
              data-testid="share-native-btn"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share via Device
            </Button>
          )}

          {/* Divider */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Connected Accounts</span>
            </div>
          </div>

          {/* Connected Platforms */}
          {loadingConnections ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : hasConnections ? (
            <div className="space-y-2">
              {connections.map((conn) => {
                const Icon = platformIcons[conn.platform] || Link2;
                return (
                  <div 
                    key={conn.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                        <Icon className="w-4 h-4 text-[#0a2540]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0a2540]">{conn.account_label}</p>
                        {conn.is_favourite && (
                          <span className="text-xs text-[#d4af37] flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            Primary
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      disabled={posting === conn.id}
                      className="bg-[#0a2540]"
                      onClick={() => {
                        toast.info('Direct posting coming soon! Copy caption for now.');
                      }}
                      data-testid={`post-to-${conn.platform}-btn`}
                    >
                      {posting === conn.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Post'
                      )}
                    </Button>
                  </div>
                );
              })}

              <Button
                variant="ghost"
                size="sm"
                onClick={handleConnectPlatform}
                className="w-full text-slate-600"
                data-testid="manage-connections-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Manage Connections
              </Button>
            </div>
          ) : (
            <div className="text-center py-4">
              <Link2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 mb-3">
                {isFree 
                  ? 'Subscribe to connect social accounts'
                  : 'Connect accounts to post directly'
                }
              </p>
              <Button
                onClick={handleConnectPlatform}
                variant="outline"
                className="border-[#0a2540] text-[#0a2540]"
                data-testid="connect-accounts-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {isFree ? 'Subscribe & Connect' : 'Connect Accounts'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostAssetDialog;
